import type { UploadItem } from '@/hooks/useBatchUpload';
import { cn, formatFileSize } from '@/utils';
import {
  AlertCircle,
  Check,
  Copy,
  File,
  FileSpreadsheet,
  FileText,
  Hash,
  Image,
  Loader2,
  Mail,
  RefreshCw,
  X,
} from 'lucide-react';
import React from 'react';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getFileIcon(fileName: string): React.ReactNode {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? '';
  const cls = 'h-3.5 w-3.5 shrink-0';

  if (['pdf'].includes(ext)) return <FileText className={cn(cls, 'text-red-400')} />;
  if (['doc', 'docx'].includes(ext)) return <FileText className={cn(cls, 'text-blue-400')} />;
  if (['xls', 'xlsx', 'csv'].includes(ext))
    return <FileSpreadsheet className={cn(cls, 'text-green-400')} />;
  if (['png', 'jpg', 'jpeg', 'tiff', 'tif', 'webp', 'heic'].includes(ext))
    return <Image className={cn(cls, 'text-violet-400')} />;
  if (['eml', 'msg'].includes(ext)) return <Mail className={cn(cls, 'text-amber-400')} />;
  return <File className={cn(cls, 'text-gray-400')} />;
}

function statusBadge(item: UploadItem): React.ReactNode {
  switch (item.status) {
    case 'queued':
      return <span className="text-[10px] text-gray-500">Queued</span>;
    case 'hashing':
      return (
        <span className="flex items-center gap-1 text-[10px] text-gray-400">
          <Hash className="h-3 w-3 animate-pulse" /> Hashing
        </span>
      );
    case 'uploading':
      return (
        <div className="flex items-center gap-2 min-w-[80px]">
          <div className="flex-1 h-1 bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-brand-500 rounded-full transition-all duration-300"
              style={{ width: `${item.progress}%` }}
            />
          </div>
          <span className="text-[10px] text-gray-400 w-7 text-right">{item.progress}%</span>
        </div>
      );
    case 'completed':
      return (
        <span className="flex items-center gap-1 text-[10px] text-emerald-400">
          <Check className="h-3 w-3" /> Done
        </span>
      );
    case 'duplicate':
      return (
        <span
          className="flex items-center gap-1 text-[10px] text-amber-400"
          title="This file is already in your library — nothing to upload."
        >
          <Copy className="h-3 w-3" /> Already added
        </span>
      );
    case 'failed':
      return (
        <span className="flex items-center gap-1 text-[10px] text-red-400">
          <AlertCircle className="h-3 w-3" /> Failed
        </span>
      );
    case 'cancelled':
      return <span className="text-[10px] text-gray-500">Cancelled</span>;
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface FileRowProps {
  item: UploadItem;
  onCancel: (id: string) => void;
  onRemove: (id: string) => void;
  onRetry: (id: string) => void;
}

const FileRow = React.memo<FileRowProps>(({ item, onCancel, onRemove, onRetry }) => {
  const isInProgress = item.status === 'hashing' || item.status === 'uploading';

  return (
    <div className="flex items-center gap-2 py-1.5 px-3 hover:bg-white/[0.02] group">
      {/* File icon */}
      {getFileIcon(item.file.name)}

      {/* Filename + size */}
      <div className="flex-1 min-w-0">
        <p className="text-[11px] text-gray-200 truncate">{item.file.name}</p>
        <p className="text-[10px] text-gray-500">{formatFileSize(item.file.size)}</p>
      </div>

      {/* Status */}
      <div className="shrink-0">{statusBadge(item)}</div>

      {/* Action button */}
      <div className="shrink-0 w-6 flex justify-center">
        {isInProgress && (
          <button
            onClick={() => onCancel(item.id)}
            className="text-gray-500 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
            title="Cancel"
          >
            <X className="h-3 w-3" />
          </button>
        )}
        {item.status === 'failed' && (
          <button
            onClick={() => onRetry(item.id)}
            className="text-gray-500 hover:text-brand-400 transition-colors"
            title="Retry"
          >
            <RefreshCw className="h-3 w-3" />
          </button>
        )}
        {(item.status === 'completed' || item.status === 'duplicate' || item.status === 'cancelled') && (
          <button
            onClick={() => onRemove(item.id)}
            className="text-gray-500 hover:text-gray-300 transition-colors opacity-0 group-hover:opacity-100"
            title="Remove"
          >
            <X className="h-3 w-3" />
          </button>
        )}
        {item.status === 'queued' && (
          <Loader2 className="h-3 w-3 text-gray-600 animate-spin" />
        )}
      </div>
    </div>
  );
});

FileRow.displayName = 'FileRow';

export default FileRow;
